import { useState } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Plus, Building2, Briefcase, MapPin, DollarSign, Link, FileText, AlertTriangle, ExternalLink } from "lucide-react";

const PLATFORMS = [
  "LinkedIn",
  "Naukri",
  "Indeed",
  "Glassdoor",
  "Monster",
  "ZipRecruiter",
  "Company Website",
  "Referral",
  "Other"
];

export const AddJobModal = ({ onClose, onJobAdded }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    company: "",
    url: "",
    location: "",
    salary: "",
    platform: "Other",
    description: ""
  });

  const checkDuplicate = async () => {
    if (!formData.title || !formData.company) return;
    
    try {
      const response = await axios.post(
        `${API}/jobs/check-duplicate`,
        null,
        {
          params: {
            title: formData.title,
            company: formData.company,
            url: formData.url || 'manual://check',
            location: formData.location
          },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.is_duplicate) {
        setDuplicateWarning(response.data);
      } else {
        setDuplicateWarning(null);
      }
    } catch (error) {
      console.error("Duplicate check failed:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.company.trim()) {
      toast.error("Title and company are required");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/jobs/manual`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Check if response indicates duplicate
      if (response.data.duplicate) {
        setDuplicateWarning({
          is_duplicate: true,
          ...response.data
        });
        setLoading(false);
        return;
      }

      const newApplication = {
        ...response.data.application,
        job: response.data.job
      };

      toast.success("Job added successfully!");
      onJobAdded(newApplication);
      onClose();
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to add job";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="add-job-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#2D3748]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Plus className="h-5 w-5 text-[#A3C3D8]" />
            Add Job Manually
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Duplicate Warning */}
          {duplicateWarning && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4" data-testid="duplicate-warning">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-orange-500 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-medium text-orange-800">Possible Duplicate Detected</h4>
                  <p className="text-sm text-orange-700 mt-1">{duplicateWarning.message || duplicateWarning.match_reason}</p>
                  {duplicateWarning.existing_job && (
                    <div className="mt-2 p-2 bg-white rounded border border-orange-100">
                      <p className="text-sm font-medium text-[#2D3748]">
                        {duplicateWarning.existing_job.title}
                      </p>
                      <p className="text-xs text-[#94A3B8]">
                        {duplicateWarning.existing_job.company} • {duplicateWarning.existing_application?.status}
                      </p>
                    </div>
                  )}
                  <div className="flex gap-2 mt-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        onClose();
                        // Could navigate to existing application
                      }}
                      className="text-orange-700 border-orange-300"
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      View Existing
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => setDuplicateWarning(null)}
                      className="text-[#555F67]"
                    >
                      Add Anyway
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Job Title */}
          <div className="space-y-2">
            <Label htmlFor="title" className="text-[#555F67] flex items-center gap-2">
              <Briefcase className="h-4 w-4" />
              Job Title *
            </Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              onBlur={checkDuplicate}
              placeholder="Software Engineer"
              className="border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
              required
              data-testid="job-title-input"
            />
          </div>

          {/* Company */}
          <div className="space-y-2">
            <Label htmlFor="company" className="text-[#555F67] flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Company *
            </Label>
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              onBlur={checkDuplicate}
              placeholder="Google"
              className="border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
              required
              data-testid="job-company-input"
            />
          </div>

          {/* Location & Platform Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="location" className="text-[#555F67] flex items-center gap-2">
                <MapPin className="h-4 w-4" />
                Location
              </Label>
              <Input
                id="location"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="San Francisco, CA"
                className="border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
                data-testid="job-location-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#555F67]">Platform</Label>
              <Select 
                value={formData.platform} 
                onValueChange={(value) => setFormData({ ...formData, platform: value })}
              >
                <SelectTrigger data-testid="platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PLATFORMS.map(platform => (
                    <SelectItem key={platform} value={platform}>
                      {platform}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Salary & URL Row */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="salary" className="text-[#555F67] flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Salary
              </Label>
              <Input
                id="salary"
                value={formData.salary}
                onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
                placeholder="$120k - $150k"
                className="border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
                data-testid="job-salary-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="url" className="text-[#555F67] flex items-center gap-2">
                <Link className="h-4 w-4" />
                Job URL
              </Label>
              <Input
                id="url"
                type="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://..."
                className="border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
                data-testid="job-url-input"
              />
            </div>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description" className="text-[#555F67] flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Description
            </Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Job description, requirements, notes..."
              className="min-h-[100px] resize-none border-slate-200 focus:border-[#A3C3D8] focus:ring-[#A3C3D8]/20"
              data-testid="job-description-input"
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="border-slate-200"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="bg-[#A3C3D8] hover:bg-[#8FB3C9] text-[#152A38]"
              data-testid="submit-job-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Job
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddJobModal;
